import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { addDoc, collection, serverTimestamp, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { Layout } from '../components/Layout';

interface FormState {
  name: string;
  contactName: string;
  contactEmail: string;
  adminEmails: string[];
  seatCount: number;
  termStartDate: string;
  termEndDate: string;
}

const EMPTY: FormState = {
  name: '',
  contactName: '',
  contactEmail: '',
  adminEmails: [],
  seatCount: 0,
  termStartDate: '',
  termEndDate: '',
};

export function FirmFormPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState<FormState>(EMPTY);
  const [adminEmailInput, setAdminEmailInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = <K extends keyof FormState>(field: K, value: FormState[K]) =>
    setForm(prev => ({ ...prev, [field]: value }));

  const addAdminEmail = () => {
    const email = adminEmailInput.trim().toLowerCase();
    if (!email || !email.includes('@')) return;
    if (form.adminEmails.includes(email)) {
      setAdminEmailInput('');
      return;
    }
    set('adminEmails', [...form.adminEmails, email]);
    setAdminEmailInput('');
  };

  const removeAdminEmail = (email: string) =>
    set('adminEmails', form.adminEmails.filter(e => e !== email));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (form.seatCount < 1) {
      setError('Seat count must be at least 1.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload: Record<string, any> = {
        name: form.name.trim(),
        contactName: form.contactName.trim(),
        contactEmail: form.contactEmail.trim().toLowerCase(),
        adminEmails: form.adminEmails,
        seatCount: form.seatCount,
        seatsUsed: 0,
        active: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };
      if (form.termStartDate) {
        payload.termStartDate = Timestamp.fromDate(new Date(form.termStartDate + 'T00:00:00'));
      }
      if (form.termEndDate) {
        payload.termEndDate = Timestamp.fromDate(new Date(form.termEndDate + 'T00:00:00'));
      }
      await addDoc(collection(db, 'firms'), payload);
      navigate('/firms');
    } catch (err: any) {
      setError(err.message || 'Failed to create firm.');
      setSaving(false);
    }
  };

  return (
    <Layout>
      <div style={{ maxWidth: 720 }}>
        <button className="back-btn" onClick={() => navigate('/firms')}>
          ← Back
        </button>
        <h1 className="page-title" style={{ marginBottom: 24 }}>Add Outplacement Firm</h1>

        {error && <div className="error-message" style={{ marginBottom: 20 }}>{error}</div>}

        <form onSubmit={handleSubmit}>
          {/* Basic info */}
          <Section title="Firm Info">
            <div className="form-group">
              <label className="form-label">Firm Name *</label>
              <input
                className="form-input"
                value={form.name}
                onChange={e => set('name', e.target.value)}
                placeholder="e.g. Acme Outplacement"
                required
              />
            </div>
          </Section>

          {/* Access */}
          <Section title="Admin Access">
            <div className="form-group" style={{ marginBottom: 20 }}>
              <label className="form-label">Primary Contact Name *</label>
              <input
                className="form-input"
                value={form.contactName}
                onChange={e => set('contactName', e.target.value)}
                placeholder="e.g. Jane Smith"
                required
                style={{ maxWidth: 360 }}
              />
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                Used as the sender name in candidate emails.
              </div>
            </div>
            <div className="form-group" style={{ marginBottom: 20 }}>
              <label className="form-label">Primary Contact Email *</label>
              <input
                className="form-input"
                type="email"
                value={form.contactEmail}
                onChange={e => set('contactEmail', e.target.value)}
                placeholder="admin@firm.com"
                required
                style={{ maxWidth: 360 }}
              />
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                This address becomes the firm admin login and is used for enrollment notifications.
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Additional Admin Emails</label>
              <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <input
                  className="form-input"
                  type="email"
                  value={adminEmailInput}
                  onChange={e => setAdminEmailInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addAdminEmail(); } }}
                  placeholder="another-admin@firm.com"
                  style={{ maxWidth: 320, margin: 0 }}
                />
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={addAdminEmail}
                  style={{ padding: '8px 14px', fontSize: 13, flexShrink: 0 }}
                >
                  Add
                </button>
              </div>
              {form.adminEmails.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {form.adminEmails.map(email => (
                    <span
                      key={email}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        fontSize: 12,
                        padding: '4px 10px',
                        background: 'var(--bg-secondary)',
                        border: '1px solid var(--border)',
                        borderRadius: 4,
                        fontFamily: 'monospace',
                      }}
                    >
                      {email}
                      <button
                        type="button"
                        onClick={() => removeAdminEmail(email)}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: 'var(--text-muted)',
                          padding: 0,
                          fontSize: 14,
                          lineHeight: 1,
                        }}
                        aria-label={`Remove ${email}`}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </Section>

          {/* Seats */}
          <Section title="Seat Allocation">
            <div className="form-group">
              <label className="form-label">Seat Count *</label>
              <input
                className="form-input"
                type="number"
                min={1}
                value={form.seatCount || ''}
                onChange={e => set('seatCount', parseInt(e.target.value) || 0)}
                required
                style={{ maxWidth: 160 }}
              />
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                Seats are permanently consumed at enrollment and never returned.
              </div>
            </div>
          </Section>

          {/* Term */}
          <Section title="Contract Term">
            <Grid>
              <div className="form-group">
                <label className="form-label">Term Start Date</label>
                <input
                  className="form-input"
                  type="date"
                  value={form.termStartDate}
                  onChange={e => set('termStartDate', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Term End Date</label>
                <input
                  className="form-input"
                  type="date"
                  value={form.termEndDate}
                  onChange={e => set('termEndDate', e.target.value)}
                />
              </div>
            </Grid>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
              When term ends, the scheduler automatically deactivates the firm and closes active enrollments.
              Leave blank if no fixed term.
            </div>
          </Section>

          <div style={{ display: 'flex', gap: 12 }}>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Creating…' : 'Create Outplacement Firm'}
            </button>
            <button type="button" className="btn-secondary" onClick={() => navigate('/firms')}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div style={{
        fontSize: 11,
        fontWeight: 600,
        color: 'var(--text-secondary)',
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        marginBottom: 16,
      }}>
        {title}
      </div>
      {children}
    </div>
  );
}

function Grid({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
      {children}
    </div>
  );
}
